import { useEffect, useRef, useState } from "react";
import IntroTypewriter from "@/components/IntroTypewriter";
import SlideOverPanel from "@/components/SlideOverPanel";
import PanelContent from "@/components/panels/PanelContent";
import LoopVideo from "@/components/LoopVideo";

const SECTIONS = [
  // Header ("THE BAKERS  menu") sits at box centre (top 50%). The 40/60
  // pairs bracket it symmetrically so it's dead-centre when the menu opens.
  { id: "welcome",       label: "welcome",       top: "12%", left: "21%" },
  { id: "recap",         label: "about us",      top: "22%", left: "60%" },
  { id: "timeline",      label: "wedding day",   top: "35%", left: "30%" },
  { id: "travel",        label: "travel",        top: "37%", left: "85%" },
  { id: "accommodation", label: "accommodation", top: "58%", left: "25%" },
  { id: "registry",      label: "registry",      top: "60%", left: "74%" },
  { id: "faqs",          label: "questions",     top: "82%", left: "32%" },
  { id: "rsvp",          label: "rsvp",          top: "86%", left: "68%" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Media — first 2 are full-bleed "main" backgrounds; everything after stacks
// straight (no tilt) and centred on the midline with varying sizes. All B&W.
// Videos are served as transcoded H.264 MP4s from the backend for cross-browser support.
const MEDIA = [
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/264vrxhi_000052%20copy.JPG", fullBleed: true },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/uivtq4vc_000054.JPG", fullBleed: true },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/a80wa7xr_J1201381.jpg", height: "84vh", offsetX: "-55px", offsetY: "-40px" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/srvpus0y_IMG_1567.jpg", height: "82vh", offsetX: "60px", offsetY: "-30px" },
  { type: "video", src: `${BACKEND}/api/videos/v1.mp4`, height: "72vh", offsetX: "-50px", offsetY: "45px" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/tq8u0xh6_fxn%202025-06-28%20213357.618.JPG", height: "86vh", offsetX: "65px", offsetY: "50px" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/6apcxntt_J1201451.jpg", height: "80vh", offsetX: "-60px", offsetY: "35px" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/8qn2n4sj_IMG_1251.jpg", height: "76vh", offsetX: "-70px", offsetY: "-50px" },
  { type: "video", src: `${BACKEND}/api/videos/v2.mp4`, height: "70vh", offsetX: "70px", offsetY: "55px", filter: "none", maxPlays: 2 },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_vow-gallery/artifacts/1bbpvlkt_IMG_4393.jpg", height: "82vh", offsetX: "55px", offsetY: "-45px" },
  { type: "video", src: `${BACKEND}/api/videos/v3.mp4`, height: "74vh", offsetX: "-65px", offsetY: "-40px", filter: "none", maxPlays: 1 },
];
const INITIAL_VISIBLE = 1;

export default function WeddingPage() {
  const [introDone, setIntroDone] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [activePanel, setActivePanel] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Allow any nested component to navigate to a panel by dispatching
  //   window.dispatchEvent(new CustomEvent('wedding:navigate', { detail: { id: 'accommodation' } }))
  useEffect(() => {
    const handler = (e) => {
      const id = e?.detail?.id;
      if (id) setActivePanel(id);
    };
    window.addEventListener("wedding:navigate", handler);
    return () => window.removeEventListener("wedding:navigate", handler);
  }, []);

  // Preload ALL images on mount so transitions are instant — sharper changes between items.
  useEffect(() => {
    MEDIA.forEach((m) => {
      if (m.type === "image") {
        const img = new Image();
        img.src = m.src;
      }
    });
  }, []);

  // Also warm the next 2 items aggressively (covers videos too).
  useEffect(() => {
    [visibleCount, visibleCount + 1].forEach((idx) => {
      const m = MEDIA[idx];
      if (!m) return;
      if (m.type === "image") {
        const img = new Image();
        img.src = m.src;
      } else if (m.type === "video") {
        fetch(m.src, { mode: "cors" }).catch(() => {});
      }
    });
  }, [visibleCount]);

  const autoTimerRef = useRef(null);

  const clearAutoTimer = () => {
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  };

  // Auto-advance: 10s for photos, 3s for videos.
  // Re-schedules whenever visibleCount changes; nextPhoto() also clears it synchronously
  // so a click can never compound with an imminent auto-tick.
  useEffect(() => {
    if (!introDone) return;
    const topIndex = Math.min(Math.max(visibleCount - 1, 0), MEDIA.length - 1);
    const topItem = MEDIA[topIndex];
    const delay = topItem?.type === "video" ? 3000 : 10000;
    clearAutoTimer();
    autoTimerRef.current = setTimeout(() => {
      autoTimerRef.current = null;
      setVisibleCount((c) => (c >= MEDIA.length ? INITIAL_VISIBLE : c + 1));
    }, delay);
    return clearAutoTimer;
  }, [introDone, visibleCount]);

  const lastAdvanceRef = useRef(0);
  const advance = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Dedupe: ignore if we've already advanced in the last 250ms (avoids
    // pointerdown + click double-firing on touch devices).
    const now = Date.now();
    if (now - lastAdvanceRef.current < 250) return;
    lastAdvanceRef.current = now;
    clearAutoTimer();
    setVisibleCount((c) => (c >= MEDIA.length ? INITIAL_VISIBLE : c + 1));
  };

  const nextPhoto = () => advance();

  const activeLabel = SECTIONS.find((s) => s.id === activePanel)?.label || "";

  return (
    <main
      data-testid="wedding-page"
      className="relative w-screen h-screen overflow-hidden bg-black cursor-pointer"
      onClick={advance}
    >
      {/* Media — items 1-2 are full-bleed main photos; items 3+ stack straight on midline */}
      <div className="absolute inset-0">
        {MEDIA.map((m, i) => {
          const shown = i < visibleCount && introDone;
          const baseStyle = {
            zIndex: 10 + i,
            visibility: shown ? "visible" : "hidden",
            filter: m.filter || "grayscale(100%) contrast(1.02)",
          };

          if (m.fullBleed) {
            const fullStyle = { ...baseStyle, pointerEvents: "none" };
            return m.type === "video" ? (
              <video
                key={m.src}
                data-testid={`media-${i + 1}`}
                src={m.src}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
                style={fullStyle}
              />
            ) : (
              <img
                key={m.src}
                data-testid={`media-${i + 1}`}
                src={m.src}
                alt=""
                loading="eager"
                decoding="async"
                fetchpriority={i === 0 ? "high" : "low"}
                className="absolute inset-0 w-full h-full object-cover"
                style={fullStyle}
              />
            );
          }

          // Stacked items: straight, centred on midline, varying sizes
          const stackStyle = {
            ...baseStyle,
            height: m.height,
            width: "auto",
            maxWidth: "92vw",
            transform: `translate(calc(-50% + ${m.offsetX || "0px"}), calc(-50% + ${m.offsetY || "0px"}))`,
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)",
            pointerEvents: "none",
          };

          return m.type === "video" ? (
            <LoopVideo
              key={m.src}
              data-testid={`media-${i + 1}`}
              src={m.src}
              maxPlays={m.maxPlays}
              shown={shown}
              onComplete={shown ? advance : undefined}
              className="absolute top-1/2 left-1/2 object-cover"
              style={stackStyle}
            />
          ) : (
            <img
              key={m.src}
              data-testid={`media-${i + 1}`}
              src={m.src}
              alt=""
              loading="eager"
              decoding="async"
              fetchpriority="low"
              className="absolute top-1/2 left-1/2 object-cover"
              style={stackStyle}
            />
          );
        })}
      </div>

      {/* Centre title — small, tight tracking, height matches cursor */}
      <div
        className={`absolute inset-0 z-40 flex items-center justify-center pointer-events-none transition-opacity duration-[1000ms] delay-200 ${
          introDone ? "opacity-100" : "opacity-0"
        }`}
      >
        <div
          className="flex items-center gap-8 sm:gap-10 pointer-events-auto"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            lineHeight: 1,
          }}
        >
          <button
            data-testid="title-cycler"
            type="button"
            onPointerDown={advance}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            aria-label="Add a photo"
            className="text-white text-base sm:text-lg uppercase font-medium [@media(hover:hover)]:hover:opacity-70 active:opacity-70 outline-none"
            style={{
              letterSpacing: "0.02em",
              textShadow: "0 1px 8px rgba(0,0,0,0.7)",
              WebkitTapHighlightColor: "transparent",
              touchAction: "manipulation",
            }}
          >
            THE BAKERS
          </button>

          <button
            data-testid="menu-trigger"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(true);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="text-white text-base sm:text-lg [@media(hover:hover)]:hover:opacity-70 active:opacity-70 transition-opacity outline-none"
            style={{
              letterSpacing: "0.02em",
              textShadow: "0 1px 8px rgba(0,0,0,0.7)",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            menu
          </button>
        </div>
      </div>

      {/* Intro typewriter */}
      <IntroTypewriter onComplete={() => setIntroDone(true)} />

      {/* Scattered menu — compact cream cluster-box centred on the "menu"
          trigger. Photos remain visible around it. Fades in over 2s. */}
      {menuOpen && (
        <div
          data-testid="scatter-menu"
          className="fixed inset-0 z-[110] animate-menu-fade flex items-center justify-center px-4"
          onClick={() => setMenuOpen(false)}
        >
          <div
            className="relative"
            style={{
              background: "rgba(114,29,33,0.88)",
              fontFamily: "'Cormorant Garamond', serif",
              width: "min(380px, 86vw)",
              height: "min(260px, 48vh)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {SECTIONS.map((s) => {
              const isRsvp = s.id === "rsvp";
              return (
                <div
                  key={s.id}
                  style={{
                    position: "absolute",
                    top: s.top,
                    left: s.left,
                    transform: "translate(-50%, -50%)",
                  }}
                  className="flex items-center justify-center"
                >
                  {isRsvp && (
                    <svg
                      aria-hidden
                      viewBox="0 0 100 60"
                      preserveAspectRatio="none"
                      className="absolute pointer-events-none"
                      style={{
                        width: "calc(100% + 40px)",
                        height: "calc(100% + 30px)",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%,-50%) rotate(-6deg)",
                        overflow: "visible",
                      }}
                    >
                      {/* Uneven, overlapping wobbly loops with a crayon-like
                          feDisplacementMap filter for a wax-crayon feel. */}
                      <defs>
                        <filter
                          id="crayonWobble"
                          x="-20%"
                          y="-20%"
                          width="140%"
                          height="140%"
                        >
                          <feTurbulence
                            type="fractalNoise"
                            baseFrequency="0.9"
                            numOctaves="2"
                            seed="7"
                            result="noise"
                          />
                          <feDisplacementMap
                            in="SourceGraphic"
                            in2="noise"
                            scale="2.2"
                          />
                        </filter>
                      </defs>
                      <g filter="url(#crayonWobble)">
                        <path
                          d="M 86 30 C 96 14, 78 5, 56 4 C 30 3, 6 14, 6 32 C 6 48, 26 56, 52 55 C 74 54, 90 48, 92 36 C 93 25, 84 20, 76 20"
                          fill="none"
                          stroke="#F4EBDD"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  )}
                  <button
                    type="button"
                    data-testid={`menu-item-${s.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      setActivePanel(s.id);
                    }}
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: "#F4EBDD",
                      WebkitTapHighlightColor: "transparent",
                      WebkitTextSizeAdjust: "100%",
                    }}
                    className="relative text-[16px] md:text-lg leading-none whitespace-nowrap [@media(hover:hover)]:hover:opacity-70 active:opacity-70 transition-opacity outline-none"
                  >
                    {s.label}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Slide-over */}
      <SlideOverPanel
        open={Boolean(activePanel)}
        title={activeLabel}
        onClose={() => setActivePanel(null)}
      >
        {activePanel && <PanelContent id={activePanel} />}
      </SlideOverPanel>
    </main>
  );
}
